import React, {useContext, useState, useRef} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose, graphql} from 'react-apollo';

import Modal from '@homeaway/react-modal';
import InputTokenize from '@homeaway/react-input-tokenize';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {Alert} from '@homeaway/react-alerts';

import {get, isEmpty} from 'lodash';
import {
    addGuestSubmitted,
    addGuestSucceeded,
    addGuestEmailInputted,
} from '../../../utils/EDAP/Conversation';
import {isExperimentBucketed} from '../../../utils/experimentBucket';
import SystemContext from '../../context/SystemContext';
import {toast, toasterActions} from '../../molecules/baseline/baseline';

import {
    GOG_COPY_UPDATE_EXPERIMENT,
    getTranslatedLabels,
} from '../../organisms/GuestsOfGuest/constants';
import {AddGuests} from '../../organisms/GuestsOfGuest/GuestsOfGuestMutations';
import {GuestsOfGuestsQuery} from '../../organisms/GuestsOfGuest/GuestsOfGuestsQuery';
import {updateApolloCacheForGuests} from '../../organisms/GuestsOfGuest/utils/GuestsOfGuest.util';
import {useEntryModal} from '../../context/EntryModalContext';

import translate from '../../../utils/translations';
import isValidEmail from '../../../utils/emailMixin';
import addGuestsImageSrc from '../../../images/add-guests.svg';

import './InviteGuestsEntryModal.less';

const AddGuestsImage = ({imgAlt}) => {
    return (
        <img
            alt={imgAlt}
            className="InviteGuestsEntryModal__Image"
            src={addGuestsImageSrc()}
        />
    );
};

const InviteGuestsEntryModal = ({
    actionLocation,
    addGuests,
    adults,
    children,
    conversationId,
    addToast,
    history,
    reservationArrival,
    reservationDeparture,
    reservationUUID,
    translations,
}) => {
    const [isOpen, setIsOpen] = useEntryModal();
    const [recipients, setRecipients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [existingRecipients, setExistingRecipients] = useState([]);
    const [invalidRecipients, setInvalidRecipients] = useState(0);
    const [isSendDisabled, setIsSendDisabled] = useState(true);
    const [mobileModalStep, setMobileModalStep] = useState(1);
    const {
        system: {isMobile},
    } = useContext(SystemContext);
    let guestCount = '';
    if (recipients.length - invalidRecipients) {
        guestCount = `(${recipients.length - invalidRecipients})`;
    }
    const isGogCopyUpdateExperimentBucketed = useRef(
        isExperimentBucketed(GOG_COPY_UPDATE_EXPERIMENT)
    ).current;
    const {
        ADD_CREW_HEADING,
        ADD_GUEST_IMG_ALT,
        ADD_GUESTS_LABEL,
        ADD_GUEST_SUB_HEADING,
        EMAIL_REMOVE_LABEL,
        EMAIL_SENT,
        EMAIL_TEXTBOX_LABEL,
        ERROR_SOMETHING_WENT_WRONG,
        INVITE_GUEST_MODAL_BUTTON_TEXT,
        INVITE_GUEST_MODAL_DESCRIPTION,
        INVITE_GUEST_MODAL_MOBILE_TITLE,
        INVITE_GUEST_MODAL_NOTES,
        INVITE_GUEST_MODAL_TITLE,
        SEND_INVITE_LABEL_COUNT,
    } = getTranslatedLabels({translations, guestCount});

    const removeGuestsPath = (guests) => {
        const pathName = history.location.pathname;
        const guestsIndex = pathName.indexOf('/guests');
        if (guestsIndex !== -1) {
            history.push({
                pathname: pathName.slice(0, guestsIndex),
                state: {
                    guests: guests || [],
                },
            });
        }
    };

    const handleRecipientsChange = (newRecipients, invalidTokens = []) => {
        let finalNewRecipients = [];

        if (invalidTokens.length > 0 || newRecipients.length === 0) {
            setIsSendDisabled(true);
        } else {
            setIsSendDisabled(false);
        }

        newRecipients.forEach((newRecipient) => {
            finalNewRecipients.push(newRecipient);
        });

        if (finalNewRecipients.length > existingRecipients.length) {
            addGuestEmailInputted({
                actionLocation,
                reservationUUID,
                reservationDeparture,
                reservationArrival,
                adults,
                children,
            });
        }

        setRecipients(finalNewRecipients);
        setInvalidRecipients(invalidTokens.length);
        setExistingRecipients(finalNewRecipients);
    };

    const onRequestError = () => {
        setErrorMessage(ERROR_SOMETHING_WENT_WRONG);
        setIsLoading(false);
    };

    const onRequestSentDone = (res, numberInvited = 1, resend = false) => {
        const guests = get(res, 'data.addGuests', []);
        if (!guests || isEmpty(guests)) {
            onRequestError();
        } else {
            setIsLoading(false);
            setIsOpen(false);
            setRecipients([]);

            const key = isGogCopyUpdateExperimentBucketed
                ? 'widget.trip.gog.copy.update.success.toast'
                : 'th.inbox.conversation.add.guests.toast.add';
            const toastMessage = !resend
                ? translate(
                      translations,
                      'trav-dash/trav-home',
                      'messages',
                      key,
                      {count: numberInvited}
                  )
                : EMAIL_SENT;

            addToast(
                toast(<h3 className="margin-bottom-0">{toastMessage}</h3>, {
                    autoClose: 6000,
                    closeOnClick: true,
                    type: 'inverse',
                })
            );
            removeGuestsPath(guests);
        }
    };

    const sendInvite = (reservationGuests, resend = false) => {
        addGuestSubmitted({
            actionLocation,
            reservationUUID,
            reservationArrival,
            reservationDeparture,
            adults,
            children,
        });
        setErrorMessage('');
        setIsLoading(true);
        addGuests({
            variables: {
                request: {
                    conversationUUID: conversationId,
                    reservationGuests,
                },
            },
            update: (store, mutationResponse) => {
                const addGuestsResponse = mutationResponse?.data?.addGuests;
                updateApolloCacheForGuests(
                    store,
                    addGuestsResponse,
                    conversationId,
                    GuestsOfGuestsQuery
                );
            },
        })
            .then((res) => {
                addGuestSucceeded({
                    actionLocation,
                    reservationUUID,
                    reservationArrival,
                    reservationDeparture,
                    adults,
                    children,
                });
                onRequestSentDone(res, reservationGuests.length, resend);
            })
            .catch(() => {
                onRequestError();
            });
    };

    const onSubmit = (event) => {
        let finalRecipients = [];
        event.preventDefault();
        if (recipients.length > 0 && !isSendDisabled) {
            recipients.forEach((recipient) => {
                if (isValidEmail(recipient)) {
                    finalRecipients.push({
                        email: recipient,
                    });
                }
            });
            sendInvite(finalRecipients);
        }
    };

    const desktopContent = (
        <div
            className="InviteGuestsEntryModal"
            data-testid="invite-guests-entry-modal"
        >
            <div className="InviteGuestsEntryModal__Row margin-bottom-3x">
                <div className="InviteGuestsEntryModal__Description">
                    <h2 className="margin-bottom-3x">
                        {isGogCopyUpdateExperimentBucketed
                            ? ADD_CREW_HEADING
                            : INVITE_GUEST_MODAL_TITLE}
                    </h2>
                    <p>
                        {isGogCopyUpdateExperimentBucketed
                            ? ADD_GUEST_SUB_HEADING
                            : INVITE_GUEST_MODAL_DESCRIPTION}
                    </p>
                </div>
                <AddGuestsImage imgAlt={ADD_GUEST_IMG_ALT} />
            </div>
            <div className="InviteGuestsEntryModal__Row InviteGuestsEntryModal__Row--Input">
                {errorMessage && <Alert msg={errorMessage} />}
                <InputTokenize
                    isValid={isValidEmail}
                    label={EMAIL_TEXTBOX_LABEL}
                    onChange={handleRecipientsChange}
                    removeLabel={EMAIL_REMOVE_LABEL}
                    tokens={recipients}
                />
                <p className="InviteGuestsEntryModal__Notes margin-bottom-0">
                    {INVITE_GUEST_MODAL_NOTES}
                </p>
            </div>
            <div className="InviteGuestsEntryModal__Row InviteGuestsEntryModal__Row--Button">
                <ButtonPrimary
                    className={'btn-block-xs'}
                    disabled={isSendDisabled}
                    label={SEND_INVITE_LABEL_COUNT}
                    onClick={onSubmit}
                />
            </div>
        </div>
    );

    const mobileContent =
        mobileModalStep === 1 ? (
            <div
                className="InviteGuestsEntryModalMobile"
                data-testid="invite-guests-entry-modal-mobile-step-one"
            >
                <div className="text-center margin-bottom-2x">
                    <div className="margin-bottom-2x">
                        <AddGuestsImage translations={translations} />
                    </div>
                    <div>
                        <h2 className="margin-bottom-2x">
                            {isGogCopyUpdateExperimentBucketed
                                ? ADD_CREW_HEADING
                                : INVITE_GUEST_MODAL_TITLE}
                        </h2>
                        <p className="margin-bottom-0">
                            {isGogCopyUpdateExperimentBucketed
                                ? ADD_GUEST_SUB_HEADING
                                : INVITE_GUEST_MODAL_DESCRIPTION}
                        </p>
                    </div>
                </div>
                <div className="InviteGuestsEntryModalMobile__Button-container">
                    <ButtonPrimary
                        label={
                            isGogCopyUpdateExperimentBucketed
                                ? ADD_GUESTS_LABEL
                                : INVITE_GUEST_MODAL_BUTTON_TEXT
                        }
                        onClick={() => setMobileModalStep(2)}
                    />
                </div>
            </div>
        ) : (
            <div
                className="InviteGuestsEntryModalMobile InviteGuestsEntryModalMobile__StepTwo"
                data-testid="invite-guests-entry-modal-mobile-step-two"
            >
                <div>
                    {errorMessage && <Alert msg={errorMessage} />}
                    <InputTokenize
                        isValid={isValidEmail}
                        label={EMAIL_TEXTBOX_LABEL}
                        onChange={handleRecipientsChange}
                        removeLabel={EMAIL_REMOVE_LABEL}
                        tokens={recipients}
                    />
                    <p className="margin-bottom-0">
                        {INVITE_GUEST_MODAL_NOTES}
                    </p>
                </div>
                <div className="text-center">
                    <ButtonPrimary
                        disabled={isSendDisabled}
                        label={SEND_INVITE_LABEL_COUNT}
                        onClick={onSubmit}
                    />
                </div>
            </div>
        );

    return (
        <Modal
            id="invite-guests-entry-modal"
            isContextual={isMobile && mobileModalStep !== 2}
            isLoading={isLoading}
            isOpen={isOpen}
            onClose={() => {
                setIsOpen(false);
                removeGuestsPath();
            }}
            title={
                isMobile && mobileModalStep === 2
                    ? INVITE_GUEST_MODAL_MOBILE_TITLE
                    : null
            }
        >
            {isMobile ? mobileContent : desktopContent}
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    ...state,
});

const mapDispatchToProps = {
    addToast: toasterActions.addToast,
};

export default compose(
    graphql(AddGuests, {name: 'addGuests'}),
    connect(mapStateToProps, mapDispatchToProps)
)(withRouter(InviteGuestsEntryModal));
